import {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  createRef,
} from 'react'
import { useForm, Controller, FieldError } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createPortal } from 'react-dom'
import { IconAlertTriangle } from '@tabler/icons-react'

import { RecurlyPayment } from 'api'
import { ButtonCTA } from 'components/ui/button-cta'
import Input from 'components/ui/input'
import ComboBox from 'components/ui/combobox'
import { useRecurly } from 'lib/recurly'
import { CardElementChangeEvent } from 'lib/recurly/types'
import { useUser } from 'hooks/use-user'
import { FieldWrapper } from 'components/ui/form/field-wrapper'
import Text from 'components/ui/text'
import { cn } from 'utils/classMerge'
import { translate } from 'utils/translation'
import { isFreeTrialUsed } from 'utils/freeVersionBlock'
import { NOT_TRIAL_PLANS } from 'utils/consts.mjs'
import { Button } from 'components/ui/buttons'
import { MIXPANEL_PROPS } from 'utils/mixpanel'
import { useContextSave } from 'hooks/use-context-save'

import { RecurlyContext } from '../../providers'
import { useCheckout, useUpdate } from '../../hooks'

import {
  FIELD_NAMES,
  RECURLY_FORM_CONFIG,
  InputConfig,
  COMBO_BOX_VALUES,
  CARD_FLAGS,
  CardBrand,
  THREE_D_ELEMENT_ID,
  CHECKOUT_SUBMIT_TYPE,
} from './consts'
import { CheckoutSchema, CheckoutFormValues } from './schemas'
import {
  getRecurlyErrors,
  filterCountries,
  getRecurlyInputStyles,
  CountryOption,
} from './utils'

import './styles.css'

export interface RecurlyErrorDetail {
  field: keyof typeof FIELD_NAMES
}

interface CoBadgeState {
  selectedBrand: CardBrand
  supportedBrands: CardBrand[]
  coBadgeSupport?: boolean
}

interface CoBadgeResponse {
  supportedBrands: CardBrand[]
  coBadgeSupport?: boolean
}

export interface NewCheckoutModalFormProps {
  formConfig?: (InputConfig | InputConfig[])[]
  className?: string
  confirmButton?: {
    className: string
    textClassName: string
    text: string
  }
  cancelButton?: {
    onClose: () => void
    className: string
    text: string
  }
  checkoutSuccessSource: string
}

export const NewCheckoutModalForm = forwardRef(
  (
    {
      formConfig = RECURLY_FORM_CONFIG,
      className = '',
      confirmButton,
      cancelButton,
      checkoutSuccessSource,
    }: NewCheckoutModalFormProps,
    ref,
  ) => {
    const context = useContextSave(RecurlyContext)
    const {
      isLoading,
      setIsLoading,
      recurlyErrors,
      setRecurlyErrors,
      showThreeDAuth,
      setServerError,
      interval,
      submitType,
    } = context

    const user = useUser()
    const { handleCheckout } = useCheckout()
    const { handleUpdate } = useUpdate()

    const defaultFormData: CheckoutFormValues = {
      firstName: '',
      lastName: '',
      cardNumber: { valid: false, empty: true, brand: undefined },
      expiryMonth: { valid: false },
      expiryYear: { valid: false },
      cvv: { valid: false },
      country: filterCountries(user.countryCode || '')?.[0] || null,
      postalCode: '',
    }

    const {
      register,
      handleSubmit,
      setValue,
      control,
      getValues,
      formState: { errors, isValid },
    } = useForm<CheckoutFormValues>({
      resolver: zodResolver(CheckoutSchema),
      defaultValues: defaultFormData,
      mode: 'onChange',
    })

    const recurly = useRecurly()
    const formRef = useRef<HTMLFormElement>(null)
    const [coBadge, setCoBadge] = useState<CoBadgeState | null>(null)

    const [recurlyRefs] = useState<
      Record<string, React.RefObject<HTMLDivElement>>
    >(() => {
      const refs: Record<string, React.RefObject<HTMLDivElement>> = {}

      formConfig.flat().forEach((item) => {
        const { name, type } = item
        if (type === 'fieldWrapper' && name) {
          refs[name] = createRef<HTMLDivElement>()
        }
      })

      return refs
    })

    const isTrial = !isFreeTrialUsed() && !NOT_TRIAL_PLANS.includes(interval)

    const onRecurlyInputChange = (
      value: CardElementChangeEvent,
      inputName: keyof CheckoutFormValues,
    ) => {
      if (recurlyErrors[inputName]) {
        const newErrors = { ...recurlyErrors }
        delete newErrors[inputName]
        setRecurlyErrors(newErrors)
      }
      setValue(inputName, value, {
        shouldValidate: typeof value.empty === 'boolean' ? !value.empty : false,
      })
    }

    const onSubmit = () => {
      if (!isValid) return
      setIsLoading(true)
      setServerError(null)
      recurly.token(
        formRef.current!,
        (
          err: Error | { code?: string; details?: RecurlyErrorDetail[] } | null,
          token: RecurlyPayment,
        ) => {
          if (err && 'details' in err && err.details) {
            setIsLoading(false)
            setRecurlyErrors(getRecurlyErrors(err.details))
          } else {
            setRecurlyErrors({})
            const handlePaymentCallback =
              submitType === CHECKOUT_SUBMIT_TYPE.update
                ? handleUpdate
                : handleCheckout
            handlePaymentCallback({
              token,
              checkoutSuccessSource,
              paymentMethod: MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,
            })
          }
        },
      )
    }

    const renderCardBrandImages = (cardBrand: CardBrand) => {
      if (!cardBrand || !CARD_FLAGS[cardBrand]) return null

      const brands = coBadge?.coBadgeSupport
        ? coBadge.supportedBrands
        : [cardBrand]

      return brands.map((brand) => {
        if (!(brand in CARD_FLAGS)) return null
        const validBrand = brand

        return (
          <button
            type="button"
            key={validBrand}
            className={cn(
              'tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-translate-x-0 tw-cursor-default tw-border-0 tw-bg-white',
              {
                'tw-opacity-50':
                  coBadge?.coBadgeSupport &&
                  validBrand !== coBadge.selectedBrand,
                'tw-cursor-pointer': coBadge?.coBadgeSupport,
              },
            )}
            onClick={() =>
              coBadge?.coBadgeSupport &&
              setCoBadge({ ...coBadge, selectedBrand: validBrand })
            }
          >
            <img
              width="30px"
              height="25px"
              src={CARD_FLAGS[validBrand]}
              alt={validBrand}
            />
          </button>
        )
      })
    }

    const renderInput = (input: InputConfig, index: number) => {
      const {
        type,
        label,
        placeholder,
        dataRecurly,
        Component,
        className: inputClassName,
        fieldWrapperClass,
      } = input
      const { name } = input

      if (!name) return null

      const fieldError = errors?.[name]
      const recurlyError = recurlyErrors[name]
      const error = (recurlyError || fieldError) as FieldError

      if (error?.message) error.message = translate(error.message)

      switch (type) {
        case 'input':
          return (
            <div
              className={`tw-w-full ${inputClassName || ''}`}
              key={`${label}-${index}`}
            >
              <Input
                {...register(name)}
                name={name}
                data-recurly={dataRecurly}
                placeholder={translate(placeholder)}
                label={translate(label)}
                error={error}
                disabled={isLoading}
              />
            </div>
          )

        case 'fieldWrapper':
          if (!Component) return null
          return (() => {
            const cardData = getValues(name)
            const brand =
              typeof cardData === 'object' && 'brand' in cardData
                ? cardData.brand
                : undefined
            return (
              <FieldWrapper
                ref={recurlyRefs?.[name] || null}
                className={cn(
                  'recurly-element-wrapper tw-p-0 tw-text-left',
                  fieldWrapperClass,
                )}
                inputWrapperClassName={cn(
                  'tw-relative tw-flex tw-items-center tw-w-full tw-border tw-border-solid tw-border-zinc-300 tw-rounded-md',
                  inputClassName,
                )}
                key={`${label}-${index}`}
                label={translate(label)}
                error={error}
              >
                {!!error && (
                  <IconAlertTriangle
                    className="tw-ml-3 tw-min-h-5 tw-min-w-5 tw-text-rose-600"
                    width={20}
                    height={20}
                    aria-hidden="true"
                    data-testid="error-icon"
                  />
                )}
                <Component
                  as="input"
                  {...register(name)}
                  className={cn(`recurly-element-inline tw-w-full`, {
                    'recurly-element-disabled': isLoading,
                  })}
                  onChange={(value: CardElementChangeEvent) => {
                    onRecurlyInputChange(value, name)
                  }}
                  onCoBadge={
                    name === 'cardNumber'
                      ? (res: CoBadgeResponse) =>
                          setCoBadge({
                            ...res,
                            selectedBrand: res.supportedBrands[0],
                          })
                      : undefined
                  }
                  onBlur={() => {
                    const currentRef = recurlyRefs[name]?.current
                    if (currentRef) {
                      currentRef.classList.remove('item-focused')
                    }
                  }}
                  onFocus={() => {
                    const currentRef = recurlyRefs[name]?.current
                    if (currentRef) {
                      currentRef.classList.add('item-focused')
                    }
                  }}
                  onReady={() => null}
                  onSubmit={onSubmit}
                  style={getRecurlyInputStyles({
                    content: translate(placeholder) || '',
                  })}
                />
                {name === 'cardNumber' &&
                  !recurlyErrors.cardNumber &&
                  brand &&
                  renderCardBrandImages(brand as CardBrand)}
              </FieldWrapper>
            )
          })()

        case 'controller':
          return (
            <Controller
              key={`${label}-${index}`}
              name={name}
              control={control}
              render={({ field }) => {
                const { value } = field
                const icon =
                  value && typeof value === 'object' && 'icon' in value
                    ? value.icon
                    : null

                return (
                  <ComboBox
                    size="default"
                    state={isLoading ? 'disabled' : 'default'}
                    label={translate(label)}
                    className={inputClassName || ''}
                    boxClassName="tw-w-full tw-text-left"
                    useVirtualList
                    onSelect={(selected) => field.onChange(selected)}
                    icon={icon && <div className="tw-h-5 tw-w-5">{icon}</div>}
                    options={
                      COMBO_BOX_VALUES[
                        name as keyof typeof COMBO_BOX_VALUES
                      ] as CountryOption[]
                    }
                    initialValue={getValues(name) as CountryOption}
                    position="top"
                    clearNumbers
                  />
                )
              }}
            />
          )

        default:
          return null
      }
    }

    useImperativeHandle(
      ref,
      () => ({
        refresh() {
          setIsLoading(false)
        },
        continueWithoutFreeTrial({
          data,
        }: {
          data: {
            paymentMethod: RecurlyPayment
            threeDSecureToken?: string
          }
        }) {
          handleCheckout({
            token: data.paymentMethod,
            threeDSecureToken: data.threeDSecureToken,
            noTrial: true,
            checkoutSuccessSource,
            paymentMethod: MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,
          })
        },
      }),
      [setIsLoading, handleCheckout, checkoutSuccessSource],
    )

    return (
      <div className={className}>
        <form
          data-testid="new-checkout-form"
          ref={formRef}
          className="new-checkout-modal tw-relative tw-flex tw-w-full tw-flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isLoading && <div className="tw-absolute tw-inset-0 tw-z-10" />}
          <div className="tw-flex tw-flex-col tw-gap-4">
            {formConfig.map((item, index) => {
              if (Array.isArray(item)) {
                const parentConfig = item.find((i) => i.type === 'parent')
                const parentClassName = parentConfig?.className || ''
                return (
                  <div
                    className={`tw-flex tw-gap-4 ${parentClassName}`}
                    key={index}
                  >
                    {item
                      .filter((i) => i.type !== 'parent')
                      .map((input) => {
                        return renderInput(input, index)
                      })}
                  </div>
                )
              }
              return renderInput(item, index)
            })}
          </div>
          <Text className="tw-m-0 tw-mt-2.5 tw-p-0 tw-text-left tw-text-sm tw-text-zinc-500">
            {translate('payment_info_security')}
          </Text>
          <div className="tw-mt-6 tw-flex tw-flex-col-reverse tw-gap-4 sm:tw-flex-row">
            {cancelButton?.onClose && (
              <Button
                variant="box"
                onClick={cancelButton.onClose}
                className={cancelButton.className}
                disabled={isLoading}
              >
                {translate(cancelButton.text)}
              </Button>
            )}
            <ButtonCTA
              className={confirmButton?.className || 'tw-h-[52px] tw-w-full'}
              textClassName={confirmButton?.textClassName || 'tw-text-lg'}
              type="submit"
              disabled={!isValid}
              isLoading={isLoading}
            >
              {confirmButton?.text
                ? translate(confirmButton.text)
                : translate(isTrial ? 'start_my_trial' : 'get_started')}
            </ButtonCTA>
          </div>
          {createPortal(
            <div
              className={cn(
                'tw-pointer-events-auto tw-absolute tw-left-1/2 tw-top-1/2 tw-z-[999999] tw-h-[400px] tw-w-auto tw--translate-x-1/2 tw--translate-y-1/2 tw-transform tw-bg-white tw-shadow-[0px_10px_32px_0px_rgba(32,32,32,0.08)] md:tw-h-[510px] md:tw-w-[490px]',
                { 'tw-hidden': !showThreeDAuth },
              )}
              id={THREE_D_ELEMENT_ID}
            />,
            document.body,
            THREE_D_ELEMENT_ID,
          )}
        </form>
      </div>
    )
  },
)
